import dayjs from 'utils/dayjs';
import React from 'react';
import s from './s.module.less';
import { Event, EventWithTimeOverlap } from 'types/calendar';
import { getEventColor, getEventColorSetting, getEventTitleDisplay, isCancelledEvent, isEventTimeOverlap } from 'utils/calendar';
import { Tooltip } from 'antd';
import { Popover } from 'antd';
import useCalendarColors from 'hooks/useCalendarColors';
import AddAndEditCalendarEvent from '../AddAndEditCalendarEvent';
import { KlarityService, Prospect } from 'types/common';
import EventTitle from '../EventTitle';

type Props = {
    useAdminApi?: boolean
    day: dayjs.Dayjs
    inx: number
    events: Event[],
    onEventEdit?: (event: Event) => void
    timezone?: string,
    onDayItemClick?: (dayString: string) => void,
    currentEditEventDayString?: string,
    currentFocusDayStr?: string,
    usePopLayer?: boolean,
    onEventUpdate?: () => void,
    allSpecialityData?: KlarityService[],
    allClients?: Prospect[],
    targetUrlEventId?: string,
};

const WeekDay = (props: Props) => {
    const { day, events, onEventEdit, currentEditEventDayString, usePopLayer = true, useAdminApi, timezone, onDayItemClick, onEventUpdate, allSpecialityData, allClients, targetUrlEventId } = props;
    const [colors] = useCalendarColors({ useAdminApi });

    const [currentEvent, setCurrentEvent] = React.useState<Event>();
    const [showEdit, setShowEdit] = React.useState(false);

    const hasAutoPop = React.useRef(false);

    const isCurrentDay = day.format('DD-MM-YY') === dayjs().format('DD-MM-YY');
    const eventsWithTimeOverlap: EventWithTimeOverlap[] = React.useMemo(() => {
        const ret: EventWithTimeOverlap[] = [];
        for (let i = 0; i < events.length; i++) {
            const currentEvent = events[i];
            const timeOverlapKeys: string[] = [];
            for (let j = 0; j < i; j++) {
                const event2 = events[j];
                if (isEventTimeOverlap(currentEvent, event2)) {
                    timeOverlapKeys.push(event2.id);
                }
            }
            ret.push({ ...events[i], timeOverlapKeys });
        }
        return ret;
    }, [events]);

    React.useEffect(() => {
        if (!currentEditEventDayString || currentEditEventDayString !== day.format('DD-MM-YY')) {
            setCurrentEvent(undefined);
            setShowEdit(false);
        }
    }, [currentEditEventDayString, day]);

    React.useEffect(() => {
        if (targetUrlEventId && events) {
            const target = events.find((item) => item.id + '' === targetUrlEventId);
            if (target) {
                // if (onEventEdit) {
                //     onEventEdit(target);
                //     return;
                // }
                setTimeout(() => {
                    if (hasAutoPop.current) {
                        return;
                    }
                    hasAutoPop.current = true;
                    // if (onEventEdit) {
                    //     onEventEdit(target);
                    //     return;
                    // }
                    setCurrentEvent(target);
                    setShowEdit(true);
                    onDayItemClick && onDayItemClick(day.format('DD-MM-YY'));
                }, 2500)
            }
        }
    }, [targetUrlEventId, events])

    const pendingAreaContent = () => {
        if (!showEdit) {
            return null;
        }
        return (
            <div className={s.areaWrap}>
                <AddAndEditCalendarEvent
                    onCancel={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    onSuccess={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                        onEventUpdate?.();
                    }}
                    event={currentEvent}
                    timezone={timezone}
                    isPopLayer
                    allSpecialityData={allSpecialityData}
                />
                <div
                    onClick={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    className={s.closeIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M6 18L18 6M6 6L18 18" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
            </div>
        )
    };

    // console.log({
    //     day: day.format('DD-MM-YY'),
    //     eventsWithTimeOverlap,
    // })
    return (
        <div className={s.wrap}>
            <div className={s.itemWrap}>
                <div className={s.header}>
                    <div>{day.format('ddd').toUpperCase()}</div>
                    <div className={s.headerDate}><span className={isCurrentDay ? `${s.dayLabel} ${s.today}` : s.dayLabel}>{day.format('DD')}</span></div>
                </div>
                <div
                    className={s.content}
                >
                    <div
                        className={s.eventWrap}
                        onClick={() => {
                        }}
                    >
                        {eventsWithTimeOverlap.map((evt, idx) => {
                            let startTimeDayjs = dayjs(evt.start.dateTime);
                            let endTimeDayjs = dayjs(evt.end.dateTime);
                            if (timezone) {
                                startTimeDayjs = startTimeDayjs.tz(timezone);
                                endTimeDayjs = endTimeDayjs.tz(timezone);
                            }

                            const startTimetArray = startTimeDayjs.format('HH-mm').split('-');
                            const endTimeArray = endTimeDayjs.format('HH-mm').split('-');
                            const starDay = startTimeDayjs.format('MM-DD');
                            const endDay = endTimeDayjs.format('MM-DD');

                            const startHour = parseInt(startTimetArray[0], 10);
                            const startMin = parseInt(startTimetArray[1], 10);
                            const endHour = parseInt(endTimeArray[0], 10);
                            const endMin = parseInt(endTimeArray[1], 10);
                            let timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            if (startTimeDayjs.hour() === 0) {
                                //12 AM
                                timeLabel = `${startTimeDayjs.format('HH:mm')} - ${endTimeDayjs.format('hh:mm A')}`;
                            }

                            let top = startHour * 50 + (startMin * 5) / 6;
                            const bottom = endHour * 50 + (endMin * 5) / 6;
                            if (startHour > endHour && endHour !== 0) {
                                top = 0;
                            }

                            const color = getEventColor(evt, colors);
                            const overlapCount = evt.timeOverlapKeys.length;
                            let height = bottom - top;
                            if (height === 0) {
                                //diff date
                                height = 50 * 24;
                                if (starDay === endDay) {
                                    //same date
                                    height = 20;
                                }
                            }
                            let narrowLayout = false;
                            if (height > 0 && height < 15) {
                                narrowLayout = true;
                            }
                            if (height > 15 && height < 17) {
                                height = 22;
                            }
                            let smallLine = false;
                            if (height > 25 && height < 40) {
                                smallLine = true;
                            }
                            const toolContent = (
                                <>
                                    <div className={s.toolTextTitle}>
                                        <EventTitle
                                            event={evt}
                                            allSpecialityData={allSpecialityData}
                                        />
                                    </div>
                                    <div className={s.tooltTextTime}>{timeLabel}</div>
                                </>
                            );
                            let className = narrowLayout ? `${s.event} ${s.narrowEvent}` : s.event;
                            if (evt.type === 'blocker') {
                                className = `${className} ${s.eventBlocker}`;
                            }
                            if (isCancelledEvent(evt)) {
                                className = `${className} ${s.eventCancelled}`;
                            }
                            if (smallLine) {
                                className = `${className} ${s.eventSmallLine}`;
                            }
                            const isEditing = currentEvent && currentEvent.id === evt.id && showEdit;

                            const cssColor = getEventColorSetting(evt, allClients);

                            if (isEditing) {
                                return (
                                    <Popover
                                        open
                                        zIndex={1000}
                                        placement="right"
                                        title={null} content={pendingAreaContent()} arrow={true}>
                                        <div
                                            key={evt.id}
                                            className={`${className} weekEventItem`}
                                            id={evt.id ? `app_${evt.id}` : undefined}
                                            style={{
                                                top,
                                                height: `${height}px`,
                                                background: color,
                                                right: 8,
                                                left: overlapCount * 20,
                                                color: cssColor.color,
                                                backgroundColor: cssColor.backgroundColor,
                                                border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                            }}
                                        >
                                            <p className={s.title}>
                                                <Tooltip autoAdjustOverflow color="#fff" overlayClassName={s.toolText} placement="top" title={toolContent}>
                                                    <EventTitle
                                                        event={evt}
                                                        allSpecialityData={allSpecialityData}
                                                    />
                                                    {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                                </Tooltip>
                                            </p>
                                            <p className={s.time}>{timeLabel}</p>
                                        </div>
                                    </Popover>
                                )
                            }
                            return (
                                <div
                                    key={evt.id}
                                    className={`${className} ${evt.isSample ? 'weekEventItemTourTip' : 'weekEventItem'}`}
                                    style={{
                                        top,
                                        height: `${height}px`,
                                        background: color,
                                        right: 8,
                                        left: overlapCount * 20,
                                        color: cssColor.color,
                                        backgroundColor: cssColor.backgroundColor,
                                        border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                    }}
                                    id={evt.isSample ? evt.divId : `app_${evt.id}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (usePopLayer) {
                                            setCurrentEvent(evt);
                                            setShowEdit(true);
                                            onDayItemClick && onDayItemClick(day.format('DD-MM-YY'));
                                            return;
                                        } else {
                                            if (onEventEdit) {
                                                onEventEdit(evt);
                                            }
                                        }
                                    }}
                                >
                                    <p className={s.title}>
                                        <Tooltip autoAdjustOverflow color="#fff" overlayClassName={s.toolText} placement="top" title={toolContent}>
                                            <EventTitle
                                                event={evt}
                                                allSpecialityData={allSpecialityData}
                                            />
                                        </Tooltip>
                                    </p>
                                    <p className={s.time}>{timeLabel}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WeekDay;
